import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { KeyRound, ShieldAlert } from "lucide-react";
import { toast } from "sonner";

export default function ForceChangePassword() {
  const { i18n } = useTranslation();
  const { user, refresh, logout } = useAuth();
  const navigate = useNavigate();
  const isEN = i18n.language === "en";

  const [currentPw, setCurrentPw] = useState("");
  const [newPw, setNewPw] = useState("");
  const [confirmPw, setConfirmPw] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (newPw.length < 8) { toast.error(isEN ? "Minimum 8 characters" : "Minimo 8 caratteri"); return; }
    if (newPw !== confirmPw) { toast.error(isEN ? "Passwords do not match" : "Le password non corrispondono"); return; }
    if (newPw === currentPw) { toast.error(isEN ? "New password must be different" : "La nuova password deve essere diversa"); return; }
    setLoading(true);
    try {
      await api.post("/auth/change-password", { current_password: currentPw, new_password: newPw });
      await refresh();
      toast.success(isEN ? "Password updated" : "Password aggiornata");
      navigate(user?.role === "admin" ? "/admin" : "/home", { replace: true });
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen bg-zinc-950 text-zinc-100 flex items-center justify-center p-5">
      <form onSubmit={submit} className="max-w-md w-full bg-zinc-900/60 border border-zinc-800 rounded-xl p-6 space-y-5" data-testid="force-change-password-page">
        <div>
          <p className="text-xs uppercase tracking-[0.25em] text-[#FFD700]">{isEN ? "Security" : "Sicurezza"}</p>
          <h1 className="font-display text-3xl font-bold mt-1 flex items-center gap-2">
            <KeyRound className="w-6 h-6 text-[#FFD700]" />
            {isEN ? "Set a new password" : "Imposta una nuova password"}
          </h1>
        </div>

        <div className="rounded-lg border border-amber-500/30 bg-amber-500/[0.06] p-4 flex gap-3" data-testid="force-change-info">
          <ShieldAlert className="w-5 h-5 text-amber-400 mt-0.5 flex-shrink-0" />
          <p className="text-sm text-amber-200 leading-relaxed">
            {isEN
              ? "Your account is using a temporary password provided by Rent Lupi. For security, please choose a new one to continue."
              : "Stai utilizzando una password temporanea fornita da Rent Lupi. Per sicurezza, scegline una nuova per continuare."}
          </p>
        </div>

        <div>
          <Label className="text-xs text-zinc-400">{isEN ? "Temporary password" : "Password temporanea"}</Label>
          <Input type="password" value={currentPw} onChange={(e) => setCurrentPw(e.target.value)}
                 className="bg-zinc-950 border-zinc-800 mt-1" autoComplete="current-password"
                 data-testid="force-change-current" />
        </div>
        <div>
          <Label className="text-xs text-zinc-400">{isEN ? "New password" : "Nuova password"}</Label>
          <Input type="password" value={newPw} onChange={(e) => setNewPw(e.target.value)}
                 className="bg-zinc-950 border-zinc-800 mt-1" autoComplete="new-password" minLength={8}
                 data-testid="force-change-new" />
        </div>
        <div>
          <Label className="text-xs text-zinc-400">{isEN ? "Confirm new password" : "Conferma nuova password"}</Label>
          <Input type="password" value={confirmPw} onChange={(e) => setConfirmPw(e.target.value)}
                 className="bg-zinc-950 border-zinc-800 mt-1" autoComplete="new-password" minLength={8}
                 data-testid="force-change-confirm" />
        </div>

        <Button type="submit" disabled={loading || !currentPw || !newPw || !confirmPw}
                className="w-full bg-[#FFD700] hover:bg-[#FACC15] text-black font-semibold"
                data-testid="force-change-submit">
          {loading ? "..." : (isEN ? "Save and continue" : "Salva e continua")}
        </Button>

        <button type="button" onClick={async () => { await logout(); navigate("/login"); }}
                className="block w-full text-xs text-zinc-500 hover:text-zinc-300 text-center"
                data-testid="force-change-logout">
          {isEN ? "Log out" : "Esci"}
        </button>
      </form>
    </div>
  );
}
